import React, { useEffect } from "react";
import {
  StyleSheet,
  Text,
  TouchableWithoutFeedback,
  View,
  Image,
  ViewStyle,
  TextStyle,
} from "react-native";
import { ChatListSchema } from "../../realm/schema";
import { color_F1F1F1, color_main, white } from "../../assets/style/colors";
import { Badge } from "../../components/Badge";
import { Avatar } from "../../../imsdk/api/topic";
import FastImage from "react-native-fast-image";
import { Topic } from "../../../imsdk/db/t_topic";
import { extractFirstCharacterOrEmoji } from "../../utils/CommonUtils";
import DefGroupAvatarIcon from "../../assets/svg/icon_default_group_avatar.svg";
import { CheckBoxView } from "../../components/CheckBoxView";
import { ServiceIcon } from "./components/ServiceIcon";
import pinyin from "../../utils/PinyinUtil";
import { fixRemoteUrl } from "../../../imsdk/api";

interface CListItemProps {
  id: string | number; // 当前记录id
  hasRadio?: boolean;
  isChecked?: boolean;
  left: Avatar;
  badge?: number;
  avatarSize?: number;
  center?: any;
  right?: any;
  item?: ChatListSchema | Topic;
  onClick?: (id: string | number) => void;
}

export enum SystemTopic {
  Assistant = 101, // 小助手
  Collect = 102, // 收藏夹
  Notification = 103, // 通知消息
}

export const isSystemAcc = (uid: number) => uid === SystemTopic.Assistant || uid === SystemTopic.Collect || uid === SystemTopic.Notification;

const colorDict: { [key: string]: string } = {
  // 暖色系 (红色/橙色/黄色调)
  "0": "#611d87", // 鲜黄绿色 (高亮度对比)
  "1": "#6200FF", // 纯蓝色 (与a形成冷暖对比)
  "2": "#634822", // 青绿色 (中等亮度)
  "3": "#6432e5", // 深紫色 (低亮度，与c形成明暗对比)

  // 冷色系 (蓝色/紫色调)
  "4": "#660033", // 深酒红色 (低亮度，与e形成最大对比)
  "5": "#675c05", // 亮绿色 (高饱和度)
  "6": "#680099", // 深洋红色 (与g形成互补色对比)

  // 中性/混合色系
  "7": "#6900CC", // 中紫色 (与j的黄色形成互补)
  "8": "#6a2f67", // 浅青绿色 (柔和，与i形成对比)
  "9": "#6B5500", // 深棕色 (低亮度，作为基础色)

  // 暖色系 (红色/橙色/黄色调)
  "a": "#611d87", // 鲜黄绿色 (高亮度对比)
  "b": "#6200FF", // 纯蓝色 (与a形成冷暖对比)
  "c": "#634822", // 青绿色 (中等亮度)
  "d": "#6432e5", // 深紫色 (低亮度，与c形成明暗对比)

  // 冷色系 (蓝色/紫色调)
  "e": "#65FFFF", // 亮青色 (最高亮度)
  "f": "#660033", // 深酒红色 (低亮度，与e形成最大对比)
  "g": "#675c05", // 亮绿色 (高饱和度)
  "h": "#680099", // 深洋红色 (与g形成互补色对比)

  // 中性/混合色系
  "i": "#6900CC", // 中紫色 (与j的黄色形成互补)
  "j": "#6a2f67", // 浅青绿色 (柔和，与i形成对比)
  "k": "#6B5500", // 深棕色 (低亮度，作为基础色)
  "l": "#6CAAFF", // 浅天蓝色 (中等亮度)

  // 增强对比色系
  "m": "#6D00FF", // 纯靛蓝色 (高饱和度)
  "n": "#6e8e21", // 亮酸橙绿 (与m形成强烈对比)
  "o": "#6F8888", // 蓝灰色 (中性，缓和视觉)
  "p": "#7099d2", // 亮草绿色 (与q形成相邻色对比)

  // 补充对比色
  "q": "#7196e9", // 灰蓝色 (中等饱和度)
  "r": "#726214", // 荧光绿 (极高亮度)
  "s": "#73659c", // 深紫色 (低亮度，与r形成对比)
  "t": "#7477ff", // 橄榄绿 (独特色调)

  // 最后确保差异性
  "u": "#75FFFF", // 极亮青色 (重复e但不同前缀)
  "v": "#76AA55", // 军绿色 (独特)
  "w": "#7700FF", // 品蓝色 (高饱和度)
  "x": "#784c19", // 亮薄荷绿 (与w形成对比)
  "y": "#790033", // 深栗色 (暖深色)
  "z": "#7A00CC"  // 中紫色调 (与y形成冷暖对比)
};

export function getColorWithUserName(name: string | number | null | undefined) {
  const nameStr =
    name == null
      ? ""
      : typeof name === "string"
        ? name
        : String(name);
  let fixname = nameStr.replace("(已删除)", "");
  let colorKey = "";
  if (fixname.length > 1) {
    // fixname = fixname.substring(0, 1);
    fixname = extractFirstCharacterOrEmoji(fixname);
    colorKey = pinyin.getCamelChars(fixname).toLowerCase();
  }
  // 获取图标底色
  const colorValue = colorDict[colorKey] || color_main;
  return colorValue;
}

export interface LeftViewProps {
  left: Avatar;
  size?: number;
  isRounded?: boolean;
  isGroup?: boolean;
  radius?: number;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

export const LeftView = (props: LeftViewProps) => {
  const {
    left,
    size,
    isRounded = true,
    isGroup = false,
    radius = 0,
    style,
    textStyle,
  } = props;

  const { name: rawName, avatar, uid } = left;
  const name =
    rawName == null
      ? ""
      : typeof rawName === "string"
        ? rawName
        : String(rawName);
  // FXIME 这里为了兼容icon的实现，有点丑，后面用新的变量隔开
  const s = size || 40;
  const isSystem = isSystemAcc(uid);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadError, setIsLoadError] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);

  if (isSystem) {
    return <ServiceIcon size={s} uid={uid} style={[{ marginRight: 12 }, style]} />;
  }

  const handleRetry = () => {
    if (isLoading || !isLoadError) {
      return; // 如果正在加载或者没有错误，不执行重试
    }
    setIsLoading(true);
    setIsLoadError(false);
    setRetryCount(prev => {
      if (prev >= 3) {
        return 0; // 重置重试次数
      }
      return prev + 1;
    });
  }

  if (avatar && avatar.length > 0) {
    return (
      <View
        style={[
          {
            marginRight: 12,
            width: s,
            height: s,
            borderRadius: radius > 0 ? radius : isRounded ? s / 2 : 4,
            overflow: "hidden",
          },
          style,
        ]}
      >
        <FastImage
          key={`${avatar}_${retryCount}`} // 通过改变key来强制刷新图片
          source={{
            uri: fixRemoteUrl(avatar),
            priority: FastImage.priority.high, // 优先级
            cache: FastImage.cacheControl.immutable, // 缓存策略
          }}
          onLoadStart={() => {
            if (retryCount === 0) {
              setIsLoading(true)
              setIsLoadError(false)
            }
          }}
          onLoadEnd={() => {
            setIsLoading(false)
            setIsLoadError(false)
          }}
          onError={() => {
            if (retryCount < 3) {
              setRetryCount(prev => prev + 1);
            } else {
              setIsLoading(false)
              setIsLoadError(true)
            }
          }}
          style={{
            width: s,
            height: s,
            borderRadius: radius > 0 ? radius : 50,
          }}
        />
      </View>
    );
  }


  let fixname = name.replace("(已删除)", "");
  let colorKey = "";
  if (fixname.length > 1) {
    // fixname = fixname.substring(0, 1);
    fixname = extractFirstCharacterOrEmoji(fixname);
    colorKey = pinyin.getCamelChars(fixname).toLowerCase();
  }
  // 获取图标底色
  const colorValue = colorDict[colorKey] || color_main;

  if (isGroup) {
    return (
      <View
        style={[{
          marginRight: 12,
          width: s,
          height: s,
          borderRadius: radius > 0 ? radius : isRounded ? s / 2 : 4,
          overflow: "hidden",
        }, style]}
      >
        <DefGroupAvatarIcon width={s} height={s} fill={colorValue} />
      </View>
    );
  }

  return (
    <View
      style={[
        styles.nameWrap,
        styles.dflex,
        styles.jccenter,
        {
          backgroundColor: colorValue,
          alignItems: "center",
        },
        style,
        {
          width: s,
          height: s,
          borderRadius: radius > 0 ? radius : isRounded ? s / 2 : 4,
        },
      ]}
    >
      <Text
        style={[{
          color: white,
          textAlign: "center",
          textAlignVertical: "center",
          fontWeight: 'bold',
          fontFamily: '',
          fontSize: 16,
        }, textStyle]}
      >
        {fixname}
      </Text>
    </View>
  );
};

export const CListItem = (current: CListItemProps) => {
  const {
    id,
    left,
    avatarSize,
    center,
    right,
    onClick,
    hasRadio,
    isChecked,
    badge,
  } = current;
  const handleOnPress = () => {
    onClick && onClick(id);
  };
  return (
    <TouchableWithoutFeedback onPress={handleOnPress}>
      <View style={[styles.itemContainer, styles.dflex]}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {hasRadio && (
            <CheckBoxView
              disabled={true}
              value={isChecked}
              style={styles.radioContainer}
              onValueChange={handleOnPress}
            />
          )}
          {/* 无图像时显示名称最后一个字 */}
          <LeftView left={left} size={avatarSize} />
          {badge! > 0 ? <Badge visible={true} children={badge} /> : null}
          <View style={[styles.contentContainer, styles.dflex]}>
            <View style={styles.centerContainer}>{center}</View>
            {right && (
              <View style={styles.rightContainer}>{current.right}</View>
            )}
          </View>
        </View>
    </View>
    </TouchableWithoutFeedback >
  );
};

const styles = StyleSheet.create({
  dflex: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  jccenter: {
    justifyContent: "center",
  },
  itemContainer: {
    backgroundColor: white,
    paddingLeft: 12,
    marginBottom: 12,
    borderRadius: 5,
    paddingVertical: 6,
  },
  radioContainer: {
    width: 24,
    height: 24,
    marginRight: 10,
  },
  img: {
    width: 40,
    height: 40,
    marginRight: 12,
  },
  nameWrap: {
    marginRight: 10,
  },
  noname: {
    backgroundColor: color_F1F1F1,
  },
  contentContainer: {
    height: 64,
    flex: 1,
    // borderBottomColor: color_F1F1F1,
    // borderBottomWidth: 1,
    paddingRight: 15,
  },
  centerContainer: {
    flex: 1,
  },
  rightContainer: {},
  badgeContainer: {
    position: "absolute",
    top: 8,
    left: 45,
  },
});

